import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';

interface UserRole {
  upn: string;
  displayName: string;
  department: string | null;
  isAdmin: boolean;
  isManager: boolean;
  roles: string[];
}

interface LeaveNotificationSettings {
  payrollEmails: string;
  financeEmails: string;
  fallbackApprovalEmails: string;
}

export const SettingsPage: React.FC = () => {
  const api = useApi();
  const { user } = useAuth();
  const { showToast } = useToast();
  const qc = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'manager' | 'user'>('all');
  const [updating, setUpdating] = useState<string | null>(null);
  const [leaveNotify, setLeaveNotify] = useState<LeaveNotificationSettings>({
    payrollEmails: '',
    financeEmails: '',
    fallbackApprovalEmails: '',
  });

  const { data: users, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-users-roles'],
    queryFn: async () => {
      const data = await api.get('/admin/users-roles');
      return data as UserRole[];
    },
    retry: 2,
    staleTime: 30000,
  });

  const leaveNotificationsQ = useQuery({
    queryKey: ['leave-notifications-settings'],
    queryFn: async () => {
      const data = await api.get('/settings/leave-notifications') as LeaveNotificationSettings;
      setLeaveNotify({
        payrollEmails: data.payrollEmails || '',
        financeEmails: data.financeEmails || '',
        fallbackApprovalEmails: data.fallbackApprovalEmails || '',
      });
      return data;
    },
    retry: 1,
    staleTime: 30000,
  });

  const saveLeaveNotifications = useMutation({
    mutationFn: async () => api.put('/settings/leave-notifications', leaveNotify),
    onSuccess: () => {
      showToast('Leave notification emails saved', 'success');
      qc.invalidateQueries({ queryKey: ['leave-notifications-settings'] });
    },
    onError: (err: any) => showToast(err?.body?.detail || 'Failed to save leave notification emails', 'error'),
  });

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    let result = users;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(u =>
        u.displayName.toLowerCase().includes(q) ||
        u.upn.toLowerCase().includes(q) ||
        (u.department || '').toLowerCase().includes(q),
      );
    }

    if (roleFilter !== 'all') {
      result = result.filter(u => u.roles.includes(roleFilter));
    }

    return result;
  }, [users, searchQuery, roleFilter]);

  const counts = useMemo(() => {
    if (!users) return { total: 0, admins: 0, managers: 0 };
    return {
      total: users.length,
      admins: users.filter(u => u.isAdmin).length,
      managers: users.filter(u => u.isManager).length,
    };
  }, [users]);

  const toggleAdmin = async (targetUpn: string, currentlyAdmin: boolean) => {
    if (targetUpn.toLowerCase() === user?.upn?.toLowerCase()) {
      showToast('You cannot revoke your own admin access', 'error');
      return;
    }

    const action = currentlyAdmin ? 'revoke' : 'grant';
    if (!confirm(`${currentlyAdmin ? 'Remove' : 'Grant'} admin access for ${targetUpn}?`)) return;

    setUpdating(targetUpn);
    try {
      if (currentlyAdmin) {
        await api.delete(`/org/admins?targetUpn=${encodeURIComponent(targetUpn)}`);
      } else {
        await api.post(`/org/admins?targetUpn=${encodeURIComponent(targetUpn)}`, {});
      }
      showToast(`Admin access ${action}d for ${targetUpn}`, 'success');
      refetch();
    } catch (err: any) {
      showToast(err?.body?.detail || `Failed to ${action} admin`, 'error');
    } finally {
      setUpdating(null);
    }
  };

  const roleBadge = (role: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      admin: { bg: '#dbeafe', color: '#1e40af' },
      manager: { bg: '#fef3c7', color: '#92400e' },
      user: { bg: '#f3f4f6', color: '#6b7280' },
    };
    const s = styles[role] || styles.user;
    return (
      <span
        key={role}
        style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
          fontSize: '11px', fontWeight: 600, marginRight: '4px',
          background: s.bg, color: s.color, textTransform: 'capitalize',
        }}
      >
        {role}
      </span>
    );
  };

  return (
    <div>
      <h2>Settings</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        Manage user access levels and permissions
      </p>

      {/* Leave notification settings */}
      <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1rem', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.35rem 0' }}>Leave Email Notifications</h3>
        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
          Set comma-separated email addresses. After approval, payroll/finance receives the approved leave details and attachments.
        </p>
        {leaveNotificationsQ.isLoading ? (
          <p style={{ color: '#6b7280' }}>Loading notification settings...</p>
        ) : leaveNotificationsQ.error ? (
          <p style={{ color: '#ef4444' }}>Failed to load leave notification settings.</p>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 760 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }}>
              Payroll / external finance email(s)
              <textarea
                value={leaveNotify.payrollEmails}
                onChange={e => setLeaveNotify(prev => ({ ...prev, payrollEmails: e.target.value }))}
                placeholder="payroll@example.com, external.finance@example.com"
                rows={2}
                style={{ fontWeight: 400, minHeight: 44 }}
              />
              <span style={{ color: '#6b7280', fontSize: 12, fontWeight: 400 }}>
                Main final recipient(s) notified after manager/admin approval.
              </span>
            </label>

            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }}>
              Additional finance email(s) optional
              <textarea
                value={leaveNotify.financeEmails}
                onChange={e => setLeaveNotify(prev => ({ ...prev, financeEmails: e.target.value }))}
                placeholder="finance@example.com"
                rows={2}
                style={{ fontWeight: 400, minHeight: 44 }}
              />
            </label>

            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }}>
              Fallback approval email(s) optional
              <textarea
                value={leaveNotify.fallbackApprovalEmails}
                onChange={e => setLeaveNotify(prev => ({ ...prev, fallbackApprovalEmails: e.target.value }))}
                placeholder="hr@example.com"
                rows={2}
                style={{ fontWeight: 400, minHeight: 44 }}
              />
              <span style={{ color: '#6b7280', fontSize: 12, fontWeight: 400 }}>
                Used as fallback/extra recipient if payroll/finance fields are blank.
              </span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <button
                onClick={() => saveLeaveNotifications.mutate()}
                disabled={saveLeaveNotifications.isPending}
                style={{ padding: '8px 16px', fontSize: 14, border: 'none', borderRadius: 6, background: '#2563eb', color: 'white', cursor: 'pointer', fontWeight: 600 }}
              >
                {saveLeaveNotifications.isPending ? 'Saving...' : 'Save Leave Emails'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ background: '#f9fafb', padding: '1rem 1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', minWidth: '120px' }}>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>{counts.total}</div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>Total Users</div>
        </div>
        <div style={{ background: '#eff6ff', padding: '1rem 1.5rem', borderRadius: '8px', border: '1px solid #bfdbfe', minWidth: '120px' }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e40af' }}>{counts.admins}</div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>Admins</div>
        </div>
        <div style={{ background: '#fffbeb', padding: '1rem 1.5rem', borderRadius: '8px', border: '1px solid #fde68a', minWidth: '120px' }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#92400e' }}>{counts.managers}</div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>Managers</div>
        </div>
      </div>

      {/* Search and filter */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, email, or department..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 1, minWidth: '250px', padding: '8px 12px', fontSize: '14px', border: '1px solid #d1d5db', borderRadius: '4px' }}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as any)}
          style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db' }}
        >
          <option value="all">All Roles</option>
          <option value="admin">Admins Only</option>
          <option value="manager">Managers Only</option>
          <option value="user">Users Only</option>
        </select>
        <span style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontSize: '14px' }}>
          {filteredUsers.length} user(s)
        </span>
      </div>

      {/* Users table */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading users...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>
          Failed to load users. Make sure you have admin access.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem' }}>Name</th>
                <th style={{ padding: '0.75rem' }}>Email</th>
                <th style={{ padding: '0.75rem' }}>Department</th>
                <th style={{ padding: '0.75rem' }}>Roles</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Admin Access</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isSelf = u.upn.toLowerCase() === user?.upn?.toLowerCase();
                return (
                  <tr key={u.upn} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem', fontWeight: 500 }}>
                      {u.displayName}
                      {isSelf && <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px' }}>(you)</span>}
                    </td>
                    <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '13px' }}>{u.upn}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280' }}>{u.department || '-'}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {u.roles.map(r => roleBadge(r))}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <button
                        onClick={() => toggleAdmin(u.upn, u.isAdmin)}
                        disabled={updating === u.upn || isSelf}
                        style={{
                          padding: '4px 14px', fontSize: '13px', borderRadius: '4px', cursor: isSelf ? 'not-allowed' : 'pointer',
                          border: u.isAdmin ? '1px solid #dc2626' : '1px solid #3b82f6',
                          color: u.isAdmin ? '#dc2626' : '#3b82f6',
                          background: 'white',
                          opacity: (updating === u.upn || isSelf) ? 0.5 : 1,
                        }}
                      >
                        {updating === u.upn ? '...' : u.isAdmin ? 'Revoke Admin' : 'Grant Admin'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
